import { useIsModalOpen } from '@codegouvfr/react-dsfr/Modal/useIsModalOpen';
import { SIZE_10MO_OCTETS } from '@common/config';
import { useCurrentCampagne } from '@common/hooks/campagne';
import { Z_SAVED_FILE } from '@common/schemas';
import { useGetSelectedCampagneRncpsFromExcelMutation } from '@common/services';
import { RncpAjoutDto } from '@common/services/rncp';
import { DsfrModal, FileUploadField, Form, FormMethodsRef } from '@common/ui';
import { appToast } from '@common/ui/toast';
import { fetchLocalFile } from '@common/utils/file';
import React, { useRef } from 'react';
import { z } from 'zod';

const ERROR_MSG = 'Le dépôt d\'un fichier Excel est obligatoire pour charger vos RNCP.';

const formSchema = z.object({
  file: Z_SAVED_FILE({
    errorMsg: ERROR_MSG,
    maxSize: SIZE_10MO_OCTETS,
  }),
});

export const LoadRncpModal = ({ dsfrModal, onLoadedRncps }: LoadRncpModalProps) => {
  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null);
  const formRef = useRef<FormMethodsRef | null>(null);

  const { campagne } = useCurrentCampagne();

  useIsModalOpen(dsfrModal, {
    onConceal: () => formRef.current?.reset(),
  });

  const [getSelectedCampagneRncpsFromExcel] = useGetSelectedCampagneRncpsFromExcelMutation();

  const handleSubmit = async ({ file }: FormResult) => {
    const formData = new FormData();

    const excelFile: File | null = await fetchLocalFile(file.url, file.name);
    if (!excelFile) return appToast.error(ERROR_MSG);

    formData.set('excel', excelFile);

    try {
      const rncps: RncpAjoutDto[] = await getSelectedCampagneRncpsFromExcel({
        id: campagne?.id,
        body: formData,
      }).unwrap();

      onLoadedRncps(rncps);
      dsfrModal.close();
      formRef.current?.reset();
    } catch {
      appToast.error('Une erreur est survenue lors du chargement du fichier Excel.');
    }
  };

  return (
    <dsfrModal.Component title="Veuillez déposer votre fichier"
                         size="large"
                         topAnchor={false}
                         buttons={[
                           {
                             children: 'ANNULER',
                             doClosesModal: true,
                             priority: 'tertiary',
                             onClick: () => formRef.current?.reset(),
                           },
                           {
                             children: 'CONFIRMER',
                             doClosesModal: false,
                             priority: 'primary',
                             onClick: () => buttonSubmitRef.current?.click(),
                           },
                         ]}>
      <Form<FormResult> className={'flex flex-col'}
                        onSubmit={handleSubmit}
                        buttonSubmitRef={buttonSubmitRef}
                        methodsRef={formRef}
                        schema={formSchema}>
        <FileUploadField name="file"
                         requiredErrorMsg="Le dépôt d'un fichier Excel est obligatoire pour charger vos RNCP"
                         extensions={['.xlsx']}
                         typesSelection={['Excel']}
                         fileTypes={[
                           'application/vnd.ms-excel',
                           'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                         ]}
                         maxSize={SIZE_10MO_OCTETS} />
      </Form>
    </dsfrModal.Component>
  );
};

interface LoadRncpModalProps {
  dsfrModal: DsfrModal;
  onLoadedRncps: (rncps: RncpAjoutDto[]) => void;
}

type FormResult = z.infer<typeof formSchema>